import { Box, Button, Flex, Spacer, Stack, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import getExcusedAbsenceFormInputs from '@/client/services/formInputs/getExcusedAbsenceFormInputs'
import mutateAndRefreshPage from '@/client/services/trpcMutations'
import { useTRPC, useTRPCClient } from '@/client/trpc'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import type { SessionDetails } from '@/components/Worlds/types'

export type ExcusedAbsenceModelProps = {
  preSelectedSessionId?: number | null
  isOpen: boolean
  onClose: () => void
  upcomingSessions: ({
    excusedAbsenceSource: string | null
    topicName: string
  } & Pick<SessionDetails, 'id' | 'startDate'>)[]
}

export default function ExcusedAbsenceModal({
  upcomingSessions,
  isOpen,
  onClose,
  preSelectedSessionId,
}: ExcusedAbsenceModelProps) {
  const toast = useToaster()
  const trpcClient = useTRPCClient()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const upcomingSessionsWithLabels = upcomingSessions.map(session => ({
    id: session.id,
    excusedAbsenceSource: session.excusedAbsenceSource,
    label: `${session.topicName} on ${session.startDate.toLocaleString()}`,
  }))

  const sessionsWithExcusedAbsences = upcomingSessionsWithLabels.filter(
    session => session.excusedAbsenceSource != null
  )

  const submitExcusedAbsenceFormInputs: FormInputType[] = useMemo(
    () =>
      // TODO: consider restricting this to sessions that are within a certain time range
      getExcusedAbsenceFormInputs({
        sessions: upcomingSessionsWithLabels.filter(
          session => session.excusedAbsenceSource == null
        ),
        preSelectedSessionId: preSelectedSessionId ?? undefined,
      }),
    [preSelectedSessionId, upcomingSessionsWithLabels]
  )

  const handleCallback = useCallback(
    async (values: FormValues) => {
      const sessionId = Number.parseInt(`${values.session_id}`, 10)
      const reason = `${values.reason_explain}`
      await mutateAndRefreshPage(
        trpcClient.series.submitExcusedAbsence.mutate({
          sessionId,
          reason,
          source: 'learner',
        })
      )
      await queryClient.invalidateQueries(trpc.series.memberAttendance.pathFilter())
      toast({
        title: 'Succesfully submitted an excused absence',
        status: 'success',
      })
      onClose()
    },
    [toast, onClose]
  )

  return (
    <ModalV3
      size="lg"
      open={isOpen}
      onClose={onClose}
      title="Submit Excused Absence"
      body={() => (
        <Stack>
          <Text align="left">
            If you are unable to attend a required session, please submit an excused absence for it.
            You will only be able to submit excused absences for sessions that are coming up soon or
            have recently ended.
          </Text>
          {sessionsWithExcusedAbsences.length > 0 ? (
            <Box>
              <Text fontWeight="bold">Excused Absences</Text>
              {sessionsWithExcusedAbsences.map(session => (
                <Text key={session.id}>
                  {session.label}
                  {session.excusedAbsenceSource === 'tutor-reschedule'
                    ? ' (created automatically due to tutor reschedule) '
                    : null}
                  {session.excusedAbsenceSource === 'tutor-attendance-change'
                    ? ' (created automatically due to an attendance policy change) '
                    : null}
                </Text>
              ))}
            </Box>
          ) : null}
          <Form
            name="SeriesExcusedAbsenceRequest"
            FooterComponent={({ submitting }) => (
              <Flex mt="16px">
                <Spacer />
                <Button variant="outline" isDisabled={submitting} onClick={onClose} mr="8px">
                  Close
                </Button>
                <Button isLoading={submitting} type="submit">
                  Submit
                </Button>
              </Flex>
            )}
            blocks={submitExcusedAbsenceFormInputs}
            onSubmit={handleCallback}
          />
        </Stack>
      )}
    />
  )
}
